import React, { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { routeName } from "Service/env";
import loadingImage from "../../../../../images/loadingicon.png";

const CheckoutForm = forwardRef(({
                                     createPaymentIntent,
                                     isNotFilled,
                                     currentEventDetails,
                                     onPaymentMethodChange,
                                 }, ref) => {
    const stripe = useStripe();
    const elements = useElements();

    const [message, setMessage] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    // Expose refreshPaymentElement method to parent component
    useImperativeHandle(ref, () => ({
        refreshPaymentElement: async () => {
            if (elements) {
                try {
                    await elements.fetchUpdates();
                    console.log('Payment Element refreshed with updated amount');
                } catch (error) {
                    console.error('Error refreshing Payment Element:', error);
                }
            }
        }
    }));

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!stripe || !elements) return;

        setIsLoading(true);
        setMessage(null);

        // Give React a chance to display the overlay.
        // PaymentElement remains mounted underneath.
        await new Promise((resolve) => setTimeout(resolve, 100));

        try {
            const returnUrl =
                (await createPaymentIntent(true)) ||
                `${window.location.origin}${routeName}/payment-result`;

            const { error, paymentIntent } =
                await stripe.confirmPayment({
                    elements,
                    confirmParams: {
                        return_url: returnUrl,
                    },
                    redirect: "if_required",
                });

            // Payment failed
            if (error) {
                console.error("Stripe payment error:", error);

                setIsLoading(false);
                setMessage(error.message);
                return;
            }

            // Payment succeeded
            if (paymentIntent?.status === "succeeded") {
                // Keep processing screen visible until navigation
                window.location.href = `${routeName}/success`;
                return;
            }

            // Klarna issue
            if (
                paymentIntent?.status === "requires_payment_method" &&
                paymentIntent.last_payment_error?.payment_method?.type === "klarna"
            ) {
                setIsLoading(false);

                setMessage(
                    "Klarna is not available for the country or address you entered. Please choose another payment method."
                );

                return;
            }

            // Don't mark other statuses as failed.
            // Keep the processing screen visible.

        } catch (err) {
            console.error("Payment exception:", err);

            setIsLoading(false);
            setMessage("Payment failed. Please try again.");
        }
    };

    return (
        <>
            <form id="payment-form" onSubmit={handleSubmit}>

                <PaymentElement
                    id="payment-element"
                    options={{
                        fields: {
                            billingDetails: {
                                name: "auto",
                                email: "auto",
                                address: "auto"
                            }
                        }
                    }}
                    onChange={(e) => {
                        const method = e.value?.type || null;

                        onPaymentMethodChange?.((prev) =>
                            prev === method ? prev : method
                        );
                    }}
                />

                <div className="purchase-button">
                    <button
                        type="submit"
                        style={{
                            backgroundColor: isNotFilled
                                ? (
                                    (currentEventDetails?.EventColor || "#c31491") +
                                    "66"
                                )
                                : (
                                    currentEventDetails?.EventColor ||
                                    "#c31491"
                                ),
                        }}
                        className="purchase-link"
                        disabled={
                            !stripe ||
                            !elements ||
                            isNotFilled ||
                            isLoading
                        }
                        id="submit"
                    >
                        Pay now
                    </button>
                </div>

                {message && (
                    <div id="payment-message">
                        {message}
                    </div>
                )}

            </form>

            {/* Keep PaymentElement mounted.
            Just display the processing overlay on top. */}
            {isLoading && (
                <div className="payment-processing-screen">
                    <div className="payment-processing-content">

                        <h1>Please wait....</h1>

                        <p>
                            We are processing your order. Do not click refresh or close this page.
                        </p>

                        <div className="payment-loading-wrapper">
                            <img
                                src={loadingImage}
                                alt="Processing payment"
                                className="payment-loading-image"
                            />
                        </div>

                    </div>
                </div>
            )}
        </>
    );
});

export default CheckoutForm;
